import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";

/**
 * `/v1/hq/marketing/*` (backend i57): overview, in-app broadcasts, and a
 * READ-ONLY view of promo codes. HUDDL issues no promo codes (Arvin, 2026-10-05):
 * a business creates its own in its Business Manager, so this client has no way
 * to create, edit or switch one. `max_discount` and `min_spend` are WHOLE PESOS;
 * `amount_off` is a fixed-2 decimal string.
 */

export type SegmentKey =
  | "all_players"
  | "business_owners"
  | "creators"
  | "creator_applicants"
  | "customers"
  | "audience";

export const SEGMENTS: { key: Exclude<SegmentKey, "audience">; label: string; description: string }[] = [
  { key: "all_players", label: "All players", description: "Everyone with a Play profile." },
  { key: "customers", label: "Customers", description: "Anyone with a confirmed or completed booking." },
  { key: "business_owners", label: "Business owners and managers", description: "Active owners and managers of a business." },
  { key: "creators", label: "Active creators", description: "Creator profiles in active status." },
  { key: "creator_applicants", label: "Creator applicants", description: "Creator profiles still applying." },
];

export interface SegmentSize {
  in_segment: number;
  opted_out: number;
  eligible: number;
}

export interface MarketingOverview {
  vouchers: {
    total: number;
    live: number;
    inactive: number;
    expired: number;
    exhausted: number;
    redemptions: number;
  };
  notifications: { status: string; type: string; count: number }[];
  deliveries: { status: string; channel: string; count: number }[];
  audiences: { status: string; count: number }[];
  templates: { status: string; count: number }[];
  broadcasts_30d: { count: number; recipients: number };
  promotions_opted_out: number;
  segments: Record<Exclude<SegmentKey, "audience">, SegmentSize>;
}

export type VoucherStatus = "inactive" | "scheduled" | "expired" | "exhausted" | "live";

export interface VoucherDto {
  id: string;
  code: string;
  label: string | null;
  /** Null for a fixed-amount code. */
  percent_off: number | null;
  /** Pesos, fixed-2 string, for a fixed-amount code. */
  amount_off: string | null;
  max_discount: number | null;
  min_spend: number;
  max_uses: number | null;
  per_user_limit: number;
  /** Uses taken: confirmed bookings plus players still checking out. */
  used_count: number;
  active: boolean;
  starts_at: string | null;
  expires_at: string | null;
  sport_id: string | null;
  created_at: string | null;
  status: VoucherStatus;
  /** The business that issued (and funds) the code. Null = a pre-i82 code nobody can redeem. */
  business_id: string | null;
  business_name: string | null;
  business_slug: string | null;
}

/** One use of a code. consumed = a confirmed booking; reserved = still in checkout; released = given back. */
export interface VoucherRedemptionDto {
  id: string;
  status: "reserved" | "consumed" | "released";
  list_amount: string;
  discount_amount: string;
  created_at: string;
  consumed_at: string | null;
  released_at: string | null;
  user_id: string | null;
  user_name: string | null;
  booking_id: string | null;
  booking_reference: string | null;
  booking_status: string | null;
}

export interface BroadcastDto {
  id: string;
  title: string;
  body: string;
  href: string | null;
  segment_key: SegmentKey;
  segment_ref: string | null;
  recipient_count: number;
  skipped_opted_out: number;
  sent_by_user_id: string | null;
  created_at: string;
}

const BASE = `${API_VERSION_PREFIX}/hq/marketing`;

export function createGrowthApi(client: ApiClient) {
  return {
    getOverview() {
      return client.get<{ overview: MarketingOverview }>(`${BASE}/overview`);
    },
    listVouchers() {
      return client.get<{ vouchers: VoucherDto[] }>(`${BASE}/vouchers`);
    },
    /** One code with every redemption of it. Read-only. */
    getVoucher(id: string) {
      return client.get<{ voucher: VoucherDto; redemptions: VoucherRedemptionDto[] }>(`${BASE}/vouchers/${id}`);
    },
    segmentSize(segment: SegmentKey, audienceId?: string) {
      const qs = new URLSearchParams({ segment, ...(audienceId ? { audience_id: audienceId } : {}) });
      return client.get<{ segment: string; size: SegmentSize }>(`${BASE}/segments/size?${qs.toString()}`);
    },
    listBroadcasts() {
      return client.get<{ broadcasts: BroadcastDto[] }>(`${BASE}/broadcasts`);
    },
    /** One idempotency key per confirmed send: a double click cannot send twice. */
    sendBroadcast(body: Record<string, unknown>, idempotencyKey: string = newIdempotencyKey()) {
      return client.post<{ id: string; recipient_count: number; skipped_opted_out: number; replay: boolean }>(
        `${BASE}/broadcasts`,
        body,
        idempotencyKey,
      );
    },
  };
}
